import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { DataTable } from "~/components/data-table";
import { Business as OrgIcon } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import {
  GetSharedSpacesCountDocument,
  LoadUserInvitationsDocument,
  LoadUserInvitationsQuery,
  LoadViewerDocument,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
  useLoadUserInvitationsQuery,
} from "~/operations";
import { useViewer } from "~/providers/viewer";
import { useSnackbar } from "notistack";
import { setDocumentTitle } from "~/lib/commonUtils";
import { Loading, LoadingFailed } from "~/components/loading";
import { useRegions } from "~/providers/RegionsProvider";
import { SpaceIcon } from "~/icons";
import { RoleCell } from "~/components/Members/RoleCell";

type Invitations = NonNullable<LoadUserInvitationsQuery["invitations"]>;
type InvitationsEdge = NonNullable<NonNullable<Invitations["edges"]>[0]>;
type InvitationsNode = NonNullable<InvitationsEdge["node"]>;

const OrgIconS = styled(OrgIcon)`
  margin-right: ${(props) => props.theme.spacing(1)};
`;

const SpaceIconS = styled(SpaceIcon)`
  margin-right: ${(props) => props.theme.spacing(1)};
`;

const FlexRow = styled("div")`
  display: flex;
  align-items: center;
`;

const reSpaceOrgMrn = /\/(spaces|organizations)\/([\w\d-]+)\/{0,1}/;

const renderResource = (resourceMrn: string) => {
  const matches = resourceMrn.match(reSpaceOrgMrn);
  if (matches == null) {
    throw new Error("failed to match mrn to org/space MRN");
  }

  let icon = <SpaceIconS />;
  if (resourceMrn.includes("organizations")) {
    icon = <OrgIconS />;
  }

  return (
    <FlexRow>
      {icon} {matches[2]}
    </FlexRow>
  );
};

export function Invitations() {
  const { viewer } = useViewer();
  const { findRegion, updateSelectedRegion } = useRegions();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const regionParam = searchParams.get("region");
  const region = regionParam ? findRegion(regionParam) : undefined;
  if (region) {
    // note that this call will refresh the page if a change in region occurs
    updateSelectedRegion(region);
  }

  const [accept] = useAcceptInvitationMutation({
    refetchQueries: [
      LoadViewerDocument,
      LoadUserInvitationsDocument,
      GetSharedSpacesCountDocument,
    ],
  });

  const [decline] = useDeclineInvitationMutation({
    refetchQueries: [LoadUserInvitationsDocument],
  });

  const { error, loading, data } = useLoadUserInvitationsQuery({
    variables: { inviteeMrn: viewer?.mrn, first: 10 },
    notifyOnNetworkStatusChange: true,
  });

  if (loading) {
    return <Loading what="invitations" />;
  }

  if (error || !data?.invitations) {
    return <LoadingFailed what="invitations" />;
  }

  const handleAcceptClick = async (item: InvitationsNode) => {
    try {
      await accept({ variables: { invitationMrn: item.mrn } });
      enqueueSnackbar("Invitation has been accepted!", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to accept an invitation!", { variant: "error" });
    }
  };

  const handleDeclineClick = async (item: InvitationsNode) => {
    try {
      await decline({ variables: { invitationMrn: item.mrn } });
      enqueueSnackbar("Invitation has been declined!", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to decline an invitation!", { variant: "error" });
    }
  };

  const invitations =
    data.invitations.edges?.flatMap((edge) => edge.node ?? []) || [];

  setDocumentTitle("Invitations");

  return (
    <Box>
      {/* Heading */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Typography variant="h4" component="h2" fontWeight={700}>
          Invitations
        </Typography>
      </Box>
      {invitations.length ? (
        <DataTable>
          <TableHead>
            <TableRow>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Resource</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>State</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map((item) => (
              <TableRow key={item.mrn}>
                <TableCell>{item.senderEmail}</TableCell>
                <TableCell>{item.inviteeEmail}</TableCell>
                <TableCell>{renderResource(item.resourceMrn)}</TableCell>
                <RoleCell roles={item.roleMrns || []} />
                <TableCell>{item.state}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleAcceptClick(item)}
                    >
                      Accept
                    </Button>
                    <Button onClick={() => handleDeclineClick(item)}>
                      Decline
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
      ) : (
        <Typography color="text.secondary">
          You have no pending invitations
        </Typography>
      )}
    </Box>
  );
}
